import _ from 'lodash';
import { defaultJson } from './defaultTemplate';
import { AuthContext } from '../../Context/authContext';
import { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import routes from '../Constants/routes';
import {
  APPLICATION_CARD_STATUS_MATRIX,
  APPLICATION_STATUS,
  APPLICATION_SUB_STATUS_EMBA,
  AVAILABLE_QUALIFYING_EXAMS_WITH_INDEX,
  DOC_ASSIGN_NOMENCLATURE,
  EDUCATION_TYPES,
  PHD_TEST_RULE_CONSTRAINTS,
  RECORDTYPE_IDS,
  REGEX,
} from '../Constants/constant';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/styles';

export const getVisibleSteps = (metaData = []) => {
  let steps = [];
  const hasValues = metaData.some((items) => {
    return items;
  });

  if (metaData && hasValues) {
    for (let i = 0; i < metaData.length; i++) {
      const element = metaData[i];
      if (element) {
        steps = [...steps, ...JSON.parse(element)].flat();
      }
    }
    // steps = defaultJson;
  } else {
    steps = defaultJson;
  }

  return steps;
};

// Returns an array of field names whose type is 'upload' in the provided template
export const getUploadFieldNames = (template) => {
  if (!Array.isArray(template)) return [];

  const uploadFields = new Set();

  const collectUploads = (fields = []) => {
    fields.forEach((field) => {
      if (!field) return;

      if (field?.type === 'upload' && field?.name) {
        uploadFields.add(field.name);
      }

      if (Array.isArray(field?.fieldsArray)) {
        collectUploads(field.fieldsArray);
      }
    });
  };

  template.forEach((step) => {
    const sections = step?.sections || [];
    sections.forEach((section) => collectUploads(section?.fields || []));
  });

  return Array.from(uploadFields);
};

export const mapToCard = (arr, subStatus) =>
  arr.map((r) => ({
    ...r,
    Sub_Status__c: subStatus,
    Application_Number__c: r.applicationNumber,
    Id: r.applicationId,
    applicationFormName: r.programName,
    dueDate: r.dueDate,
    meetingLink: r.meetingLink,
  }));

export function removeNullKeys(obj) {
  if (obj === null || obj === undefined) return obj;

  // Preserve arrays: clean each element and keep array shape
  if (Array.isArray(obj)) {
    const cleanedArr = obj.map((v) => removeNullKeys(v)).filter((v) => v !== null && v !== undefined && !(typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0));
    return cleanedArr;
  }

  if (typeof obj !== 'object') return obj;

  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value === null || value === undefined) return acc;

    if (Array.isArray(value)) {
      const cleanedArray = value.map((v) => removeNullKeys(v)).filter((v) => v !== null && v !== undefined && !(typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0));
      if (cleanedArray.length > 0) acc[key] = cleanedArray;
    } else if (typeof value === 'object') {
      const cleanedValue = removeNullKeys(value);
      if (cleanedValue !== null && cleanedValue !== undefined) {
        if (Array.isArray(cleanedValue)) {
          if (cleanedValue.length > 0) acc[key] = cleanedValue;
        } else if (Object.keys(cleanedValue).length > 0) {
          acc[key] = cleanedValue;
        }
      }
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});
}

export function getDirtyValues(dirtyFields, allValues) {
  return _.pick(allValues, _.keys(dirtyFields));
}

export function removeArrayItem(obj, name, index) {
  _.unset(obj, `${name}[${index}]`);
  _.set(
    obj,
    name,
    _.get(obj, name)?.filter((e) => e)
  );
  return obj;
}

export function filterDirtyFields(dirtyFields) {
  const onlyObjs = removeEmptyObjects(dirtyFields);
  // const arraysRemoved = removeEmptyArrays(dirtyFields);
  // console.log("Onlyobjs: ", arraysRemoved);

  return { ...onlyObjs };
}

export function applicationAddCurrentStep(field, payloadApplication, { application = {} }, activeStep, next = false, _isPostFlow = false, programNameShort = '', awaitedSteps = []) {
  const applicationData = payloadApplication || application || {};
  const status = applicationData?.hed__Application_Status__c;
  const subStatus = applicationData?.Sub_Status__c;
  const isEmba = programNameShort === 'EMBA';
  const awaitedList = Array.isArray(awaitedSteps) ? awaitedSteps.filter(Boolean) : [];
  const hasAwaitedSteps = awaitedList.length > 0;

  const canUpdateStep = (() => {
    if (hasAwaitedSteps) return false;
    if (isEmba) {
      return subStatus !== APPLICATION_SUB_STATUS_EMBA[2];
    }
    return !status || status === APPLICATION_STATUS[0];
  })();

  if (!_isPostFlow && !canUpdateStep) return {};

  const stepVal = { [field]: next === true ? activeStep + 1 : activeStep };
  return { application: { ...(payloadApplication || application), ...stepVal } };
}

export const PrivateRouter = ({ children }) => {
  const {
    state: { isAuthenticated },
  } = useContext(AuthContext);

  if (isAuthenticated) {
    return children;
  }

  return <Navigate to={routes.login} />;
};

export const PublicRouter = ({ children }) => {
  const {
    state: { isAuthenticated, search, user },
  } = useContext(AuthContext);
  const { pathname } = useLocation();
  const isLogin = pathname.includes(routes.login);
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));

  if (!isAuthenticated) {
    return children;
  }

  if (search !== null && search !== '' && search !== undefined) {
    return <Navigate to={`/courses?search=${search}`} />;
  }

  if (isLogin && isMobileScreen && user?.changePasswordRequired) {
    return <Navigate to={routes.resetPasswordMain} />;
  }

  return <Navigate to={routes.home} />;
};

export const getSplitName = (combinedName, key, returnArray = false) => {
  const obj = {
    objectName: '',
    fieldName: '',
  };
  const replaceBrackets = combinedName.replace(/[\[\]']+/g, ' ')?.trim();
  const splitStr = replaceBrackets?.split(' ');
  if (Array.isArray(splitStr) && splitStr.length > 0) {
    obj.objectName = splitStr[0];
    obj.fieldName = splitStr[1];
  }
  if (key === 'objectName') return obj.objectName;
  if (key === 'fieldName') return obj.fieldName;
  if (returnArray) return splitStr;
  return obj;
};

export const returnFirstElement = (arr, firstElement) => {
  const getElement = arr?.find((items) => {
    return items.value === firstElement;
  });
  let customArr = [...arr].filter((items) => {
    return items.value !== firstElement;
  });

  return [getElement, ...customArr];
};

export const shrinkTextBased = (limit = 0, text = '') => (text.length > limit ? `${text.substring(0, limit)}...` : text);

export const handleError = (enqueueSnackbar, error) => {
  if (!error?.response?.data?.success) {
    enqueueSnackbar(error?.response?.data?.errorMessage || 'Something went wrong', { variant: 'error' });
  } else {
    enqueueSnackbar(error?.message || 'Something went wrong', {
      variant: 'error',
    });
  }
};

export function bytesToMB(bytes) {
  return (bytes / (1024 * 1024)).toFixed(2);
}

export function findMimeType(type) {
  const mimeTypes = {
    WORD_X: 'application/msword',
    // Add more types here as needed
  };
  return mimeTypes[type] || 'application/pdf';
}

export const customizeArrayFields = (mainSchema, name, index) => {
  let schema = { ...mainSchema };

  // Destructuring Field Data for fieldSchemaValidation
  if (schema?.validateField) {
    let fieldSchemaName = { ...schema?.validateField, scope: schema?.validateField?.strictComparison ? schema?.validateField.scope : `${name}.${index}.${schema?.validateField.scope}` };
    schema.validateField = fieldSchemaName;
  }
  //Destructuring the name if any rule is applied to the field
  if (schema?.rule) {
    let scopeName = schema.rule.condition.scope;
    let scopeValue = schema.rule.condition.expectedValue;

    if (typeof scopeValue !== 'string') {
      let newObjectValues = {};
      const expectedValueObj = schema.rule.condition.expectedValue;
      const expectedValueArray = Object.keys(expectedValueObj);
      for (let i = 0; i < expectedValueArray.length; i++) {
        const element = expectedValueArray[i];
        newObjectValues[`${name}.${index}.${element}`] = expectedValueObj[element];
      }

      schema.rule.condition.expectedValue = newObjectValues;
    }

    if (typeof scopeName === 'string') {
      schema.rule.condition.scope = `${name}.${index}.${scopeName}`;
    } else {
      schema.rule.condition.scope = schema.rule.condition.scope?.map((items) => {
        return `${name}.${index}.${items}`;
      });
    }
  }

  // For Formula Schema
  if (schema?.formulas) {
    const newArrayFields = schema?.formulas?.scopeFields;
    let newObj = {};
    Object.keys(newArrayFields).forEach((items) => {
      newObj[items] = `${name}.${index}.${newArrayFields[items]}`;
    });
    schema['formulas']['scopeFields'] = newObj;
  }

  // For Mindate/Maxdate Schema
  if (typeof schema?.minDate === 'object') {
    schema['minDate']['scope'] = schema?.minDate?.constant ? schema?.minDate?.scope : `${name}.${index}.${schema['minDate']['scope']}`;
    if (typeof schema['minDate']?.add?.value === 'object') {
      schema['minDate']['add']['value']['scope'] = `${name}.${index}.${schema?.minDate?.add?.value?.scope}`;
    }
  }
  if (typeof schema?.maxDate === 'object') {
    schema['maxDate']['scope'] = schema?.maxDate?.constant ? schema?.maxDate?.scope : `${name}.${index}.${schema['maxDate']['scope']}`;
  }

  if (schema?.metaData?.valueKey) {
    schema['metaData']['valueKey'] = `${name}.${index}.${schema['metaData']['valueKey']}`;
  }
  if (schema?.metaData?.editCountField) {
    schema['metaData']['editCountField']['api'] = `${name}.${index}.${schema['metaData']['editCountField']['api']}`;
  }

  return schema;
};

export function parseFieldKey(key) {
  const regex = REGEX.ARRAYFIELDNAME;
  const match = key.match(regex);

  if (match) {
    const [, base, index, field] = match;
    return { base, index, field };
  } else {
    return null;
  }
}

export const phDTestRule = (applicantType = '', areaOfSpecialisation = '') => {
  const match = Object.entries(PHD_TEST_RULE_CONSTRAINTS).find(([_, rule]) => {
    const isApplicantMatch = rule.applicantType === applicantType;
    const spec = rule.areaOfSpecialisation;
    const isSpecMatch = Array.isArray(spec) ? spec.includes(areaOfSpecialisation) : spec === areaOfSpecialisation;
    return isApplicantMatch && isSpecMatch;
  });

  return match?.[0];
};

export const modifyPayload = (mainData, applicationState, recordsTypes) => {
  let data = { data: { ...mainData } };

  if (
    data?.data?.application?.Applicant_Type__c === 'Domestic' ||
    (data?.data?.application?.Applicant_Type__c === 'International' && data?.data?.application?.Category__c === 'Overseas Candidate (holding Indian Passport) (OS-CND)')
  ) {
    data.data['application']['hed__Citizenship__c'] = data?.data?.application?.hed__Citizenship__c ? data?.data?.application?.hed__Citizenship__c : 'Indian';
  }

  if (data?.data?.application?.Applicant_Type__c === 'International') {
    data.data['application']['Eligibility_Test_Intr__c'] = data?.data?.application?.Eligibility_Test__c;
  }

  // Ref: IIMU-JIRA-234
  if (data?.data?.application?.Eligibility_Test__c) {
    if (data?.data?.application?.Eligibility_Test__c === 'GMAT') {
      if (data?.data?.application?.Applicant_Type__c === 'International') {
        data.data['application']['GMAT_Total_Score_Intr'] = data?.data?.application?.GMAT_GRE_total_score_CAT_Percentile__c;
      } else {
        data.data['application']['GMAT_Total_Score'] = data?.data?.application?.GMAT_GRE_total_score_CAT_Percentile__c;
      }
    } else if (data?.data?.application?.Eligibility_Test__c === 'CAT') {
      data.data['application']['CAT_Total_Score'] = data?.data?.application?.GMAT_GRE_total_score_CAT_Percentile__c;
    } else if (data?.data?.application?.Eligibility_Test__c === 'GRE') {
      if (data?.data?.application?.Applicant_Type__c === 'International') {
        data.data['application']['GRE_Total_Score_Intr'] = data?.data?.application?.GMAT_GRE_total_score_CAT_Percentile__c;
      } else {
        data.data['application']['GRE_Total_Score'] = data?.data?.application?.GMAT_GRE_total_score_CAT_Percentile__c;
      }
    } else if (data?.data?.application?.Eligibility_Test__c === 'GMAT Focus') {
      if (data?.data?.application?.Applicant_Type__c === 'International') {
        data.data['application']['GMAT_Focus_Total_Score_Intr'] = data?.data?.application?.GMAT_GRE_total_score_CAT_Percentile__c;
      } else {
        data.data['application']['GMAT_Focus_Total_Score'] = data?.data?.application?.GMAT_GRE_total_score_CAT_Percentile__c;
      }
    } else {
      //....
    }
  }

  if (data?.data?.application?.Applicant_Type__c === 'International') {
    delete data.data['application']['Eligibility_Test__c'];
  }

  if ((data.data?.addresses || [])?.length === 0) {
    if (data.data.hasOwnProperty('addresses')) {
      data.data['addresses'].push({
        Is_permanent_address_same_as_current_add__c: 'true',
      });
    } else {
      data.data['addresses'] = [
        {
          Is_permanent_address_same_as_current_add__c: 'true',
        },
      ];
    }
  }

  const newExamination = [];
  for (let i = 0; i < AVAILABLE_QUALIFYING_EXAMS_WITH_INDEX.length; i++) {
    const course = AVAILABLE_QUALIFYING_EXAMS_WITH_INDEX[i];
    const entranceData = data.data.examinations?.find((item) => {
      return item?.Exam_Name__c === course;
    });

    newExamination.push({
      ...entranceData,
      Exam_Name__c: course,
      UPSC_Mains_Score_Optional__c: entranceData?.UPSC_Mains_Score__c,
    });
  }
  data.data.examinations = newExamination;

  let newEducationHistories;

  const handleMarks = (items) => {
    if (items.Evaluation_type__c === 'Percentage' && items?.Maximum_CGPA_GPA_Grade_Percentage_Points__c && items?.Obtained_CGPA_GPA_Grade_Percentage_Point__c && items?.Equivalent_Percentage__c) {
      items['Maximum_CGPA_GPA_Grade_Percentage_Points__c_II'] = items['Maximum_CGPA_GPA_Grade_Percentage_Points__c'];
      items['Obtained_CGPA_GPA_Grade_Percentage_Point__c_II'] = items['Obtained_CGPA_GPA_Grade_Percentage_Point__c'];
      items['Equivalent_Percentage__c_II'] = items['Equivalent_Percentage__c'];

      delete items['Maximum_CGPA_GPA_Grade_Percentage_Points__c'];
      delete items['Obtained_CGPA_GPA_Grade_Percentage_Point__c'];
      delete items['Equivalent_Percentage__c'];
    }

    return items;
  };

  if (data?.data?.educationHistories) {
    newEducationHistories = {
      educationHistoriesUg: (data?.data?.educationHistories || [])
        ?.filter((items) => {
          return items?.Degree_Type__c === EDUCATION_TYPES['educationHistoriesUg'];
        })
        .map((items) => {
          const duration = items?.Duration_of_the_Program__c ? parseInt(items.Duration_of_the_Program__c.split(' ')[0], 10) || null : null;
          return {
            ...handleMarks(items),
            Duration_of_the_Program__c: duration,
          };
        }),
      educationHistoriesOthers: (data?.data?.educationHistories || [])
        ?.filter((items) => {
          return (
            items?.Degree_Type__c !== EDUCATION_TYPES['educationHistoriesUg'] &&
            items?.Degree_Type__c !== EDUCATION_TYPES['educationHistories[0]'] &&
            items?.Degree_Type__c !== EDUCATION_TYPES['educationHistories[1]']
          );
        })
        .map((items) => {
          const duration = items?.Duration_of_the_Program__c ? parseInt(items.Duration_of_the_Program__c.split(' ')[0], 10) || null : null;
          return {
            ...handleMarks(items),
            Duration_of_the_Program__c: duration,
          };
        }),
      educationHistoriesPhd: (data?.data?.educationHistories || [])
        .map((items) => {
          const duration = items?.Duration_of_the_Program__c ? parseInt(items.Duration_of_the_Program__c.split(' ')[0], 10) || null : null;
          return {
            ...handleMarks(items),
            Duration_of_the_Program__c: duration,
          };
        })
        .filter((items) => {
          return items?.Degree_Type__c !== EDUCATION_TYPES['educationHistories[0]'] && items?.Degree_Type__c !== EDUCATION_TYPES['educationHistories[1]'];
        }),
      educationHistories: (data?.data?.educationHistories || [])
        ?.filter((items) => {
          return items?.Degree_Type__c === EDUCATION_TYPES['educationHistories[0]'] || items?.Degree_Type__c === EDUCATION_TYPES['educationHistories[1]'];
        })
        .map((items) => {
          return handleMarks(items);
        }),
    };
    delete data.data['educationHistories'];
  }

  const customRecords = { ...data.data };
  const employmentrecordIds = recordsTypes?.['workExperiences'] ? recordsTypes['workExperiences'] : applicationState?.recordTypeIds?.['workExperiences'];
  Object.keys(RECORDTYPE_IDS['workExperiences']).forEach((items) => {
    if (customRecords?.workExperiences || [].length > 0) {
      const newData =
        customRecords?.workExperiences?.filter((subitems) => {
          return subitems?.RecordTypeId === employmentrecordIds[RECORDTYPE_IDS['workExperiences'][items]];
        }) || [];
      data.data[`${items}`] = newData;
    }
  });

  const IS_POSTFLOW = APPLICATION_CARD_STATUS_MATRIX['POSTAPPLICATION'].includes(data.data?.application?.hed__Application_Status__c);
  const HAS_PASSPORT = data.data?.applicant?.Passport_Number__c;
  let newDocumentChecklist = {};
  const MASTER_COPY = data?.data?.documentChecklists || [];

  const documentChecklist = MASTER_COPY.map((items) => {
    return {
      ...items,
      value: items?.Submitted_File_Url__c ? 'file' : null,
      label: items?.Document_Type__c,
      Document_Requirement__c:
        items?.Document_Type__c === 'Passport' && IS_POSTFLOW ? (HAS_PASSPORT && HAS_PASSPORT !== '' ? 'Mandatory' : items?.Document_Requirement__c) : items?.Document_Requirement__c,
      helperText: `${(items?.Accepted_File_Types__c || 'JPG')?.replace(';', ',').toLowerCase()} formats supported . Max file size ${items?.Maximum_File_Size__c || 2} MB`,
    };
  }).filter((items) => {
    return IS_POSTFLOW
      ? APPLICATION_CARD_STATUS_MATRIX['POSTAPPLICATION'].includes(items?.Application_Status__c)
      : !APPLICATION_CARD_STATUS_MATRIX['POSTAPPLICATION'].includes(items?.Application_Status__c);
  });

  for (let i = 0; i < documentChecklist.length; i++) {
    const element = documentChecklist[i];
    if (element?.Document_Category__c) {
      if (newDocumentChecklist.hasOwnProperty(element?.Document_Category__c)) {
        newDocumentChecklist[element?.Document_Category__c].push(element);
      } else {
        newDocumentChecklist[element?.Document_Category__c] = [element];
      }
    } else {
      if (newDocumentChecklist.hasOwnProperty('Others')) {
        newDocumentChecklist['Others'].push(element);
      } else {
        newDocumentChecklist['Others'] = [element];
      }
    }
  }

  const phdOptions = phDTestRule(data.data?.application?.Applicant_Type__c, data.data?.application?.Area_of_Specialisation_Applying_For__c);

  return {
    ...data.data,
    ...newEducationHistories,
    documentChecklists: newDocumentChecklist,
    originalDocumentChecklists: documentChecklist,
    form: {
      endDate: applicationState?.form?.endDate,
      phdOptions,
    },
  };
};

export const triggerFieldError = (errors) => {
  const errorFields = [];
  for (const i in errors) {
    const value = errors[i];
    if (Array.isArray(value)) {
      for (const k in value) {
        for (const z in value[k]) {
          errorFields.push(`${i}.${k}.${z}`);
        }
      }
    } else {
      for (const z in value) {
        errorFields.push(`${i}[${z}]`);
      }
    }
  }

  return errorFields;
};

function removeFalseKeys(obj) {
  const t = _(obj)
    .pickBy(_.isBoolean)
    .omitBy((o) => {
      return !o;
    })
    .value();

  const t1 = _(obj).omitBy(_.isBoolean).value();
  return { ...t, ...t1 };
}

function removeEmptyObjects(obj) {
  const oo = removeFalseKeys(obj);

  return (
    _(oo)
      .pickBy(_.isObject)
      // .omitBy(_.isArray)
      .mapValues(removeEmptyObjects)
      .omitBy(_.isEmpty)
      .omitBy((o) => {
        const isValid =
          Object.keys(o).filter((key) => {
            return o[key];
          }).length === 0;

        return isValid;
      })
      .assign(_.omitBy(oo, _.isObject))
      .value()
  );
}

// Optimized downloadFile function
export function downloadFile(pdfUrl, fileName = 'download.pdf') {
  fetch(pdfUrl)
    .then((response) => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.blob();
    })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    })
    .catch((error) => console.error('Download failed:', error));
}

export function downloadBase64File(base64Data, fileName, mimeType = 'application/pdf') {
  const linkSource = `data:${mimeType};base64,${base64Data}`;
  const downloadLink = document.createElement('a');
  downloadLink.href = linkSource;
  downloadLink.download = fileName;
  downloadLink.click();
}

export function fileToBase64(file) {
  if (!file) {
    return null;
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

export const extractLastFieldName = (str) => {
  if (/\[|\./.test(str)) {
    const match = str.match(/(?:\[([^\[\]]+)\]|\.([^.\[\]]+))$/);
    return match ? match[1] || match[2] : str;
  }
  return str;
};

export const getScopeFields = (array, name) => {
  const finalName = extractLastFieldName(name);
  let selectedField;

  for (let i = 0; i < array.length; i++) {
    const sections = array[i]?.sections || [];
    for (let j = 0; j < sections.length; j++) {
      const fields = sections[j]?.fields || [];
      for (let k = 0; k < fields.length; k++) {
        const field = fields[k];

        if (field?.type === 'array') {
          const fieldArray = field?.fieldsArray || [];
          for (let l = 0; l < fieldArray.length; l++) {
            const arrayField = fieldArray[l];
            if (arrayField?.name === finalName) {
              selectedField = arrayField;
            }
          }
        } else {
          if (field?.name === finalName) {
            selectedField = field;
          }
        }
      }
    }
  }

  return selectedField;
};

export function handleApiErrors(error, options = {}) {
  const { navigate, enqueueSnackbar, redirectTo, redirectMessageMatch = 'Duplicate applications' } = options;
  const messages = error?.response?.data?.errorMessages ?? [];

  if (!messages.length) {
    enqueueSnackbar?.('Something went wrong while saving the form data', { variant: 'error' });
    return;
  }

  for (const { errorMessage } of messages) {
    const message = errorMessage || 'Something went wrong while saving the form data';
    if (redirectTo && message.includes(redirectMessageMatch)) {
      navigate?.(redirectTo, { replace: true });
      return;
    }

    enqueueSnackbar?.(message, { variant: 'error' });
  }
}

export const parseApiError = (err, options) => {
  try {
    if (err?.response?.data?.message) {
      return JSON.parse(err.response.data.message).errorMessages.map((e) => e.errorMessage);
    }
    if (err?.response?.data?.errorMessages) {
      return err.response.data.errorMessages.map((e) => e.errorMessage);
    }
  } catch (e) {
    return ['Something went wrong while parsing the error.'];
  }
  return [err?.message || 'Request failed'];
};

export const findDupes = (data) => {
  for (let i = 0; i < data.length; i++) {
    const element = data[i];
    const findDupes = data.find((sub, index) => {
      return index !== i && sub?.Degree_Name__c === element?.Degree_Name__c && sub?.Discipline__c === element?.Discipline__c;
    });
    if (findDupes) {
      return true;
    }
  }

  return false;
};

export const base64ToObjectUrl = async (base64) => {
  return await fetch(base64)
    .then((r) => r.blob())
    .then(URL.createObjectURL);
};

export const resetForm = (data, reset) => {
  reset(data, {
    keepDirty: false,
    keepErrors: false,
    keepDirtyValues: false,
    keepTouched: false,
    keepValues: false,
  });
};

export const gridSystem = (ui = {}) => {
  switch (ui.gridType) {
    case 'fullRow':
      return { xs: 12, sm: 12, md: 12, lg: 12 };
    case 'halfRow':
      return { xs: 12, sm: 6, md: 6, lg: 6 };
    default:
      return {
        xs: ui.grid?.xs || 12,
        sm: ui.grid?.sm || 6,
        md: ui.grid?.md || 6,
        lg: ui.grid?.lg || 4,
      };
  }
};

export const getFieldSize = (steps = []) =>
  steps.map((step) => ({
    ...step,
    sections: (step.sections || []).map((section) => ({
      ...section,
      fields: (section.fields || []).map((field) => {
        if (field.type === 'array') {
          return {
            ...field,
            fieldsArray:
              field.fieldsArray?.map((inner) => ({
                ...inner,
                ui: gridSystem(inner.ui),
              })) || [],
          };
        }

        return {
          ...field,
          ui: gridSystem(field.ui),
        };
      }),
    })),
  }));
